import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import BaseFormModal from '../ui/BaseFormModal';
import DatePicker from '../ui/DatePicker';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { Project } from '../../types';
import {
  type DurationUnit,
  daysToDisplay,
  toDays,
  addDurationToLocalDate,
  formatRightsDurationLabel,
} from '../../utils/rightsDuration';

export interface DiffusionRightsPayload {
  startDate: Date;
  platforms: string[];
  /** Duration in days for DB storage / display round-trip */
  duration: number;
  /** UI unit used to compute the calendar end date */
  durationUnit: DurationUnit;
  durationValue: number;
  territory: string;
  exclusivity: boolean;
}

interface DiffusionStartedFormData {
  startDate: Date | null;
  platforms: string;
  territory: string;
  duration: number;
  durationUnit: DurationUnit;
  exclusivity: boolean;
}

interface DiffusionStartedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: DiffusionRightsPayload) => void;
  project: Project | null;
}

const DiffusionStartedModal: React.FC<DiffusionStartedModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  project,
}) => {
  const { t } = useI18n();
  const { formatDateLocale } = useTranslations();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<DiffusionStartedFormData>({
    defaultValues: {
      startDate: new Date(),
      platforms: '',
      territory: 'France',
      duration: 12,
      durationUnit: 'months',
      exclusivity: false,
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    const rights = project?.rights;
    const display = rights?.duration
      ? daysToDisplay(rights.duration)
      : { value: 12, unit: 'months' as DurationUnit };
    const existingStart = rights?.startDate
      ? rights.startDate instanceof Date
        ? rights.startDate
        : new Date(rights.startDate)
      : new Date();

    reset({
      startDate: existingStart,
      platforms: rights?.platforms?.join(', ') || '',
      territory: rights?.territory || 'France',
      duration: display.value,
      durationUnit: display.unit,
      exclusivity: !!rights?.exclusivity,
    });
  }, [isOpen, project, reset]);

  const startDate = watch('startDate');
  const durationValue = Number(watch('duration')) || 0;
  const durationUnit = watch('durationUnit') || 'months';
  const durationDays = toDays(durationValue, durationUnit);
  const endDate =
    startDate && durationValue > 0
      ? addDurationToLocalDate(startDate, durationValue, durationUnit)
      : null;
  const isEditing = !!project?.rights?.startDate;

  const handleConfirm = (data: DiffusionStartedFormData) => {
    if (!data.startDate) return;
    const value = Number(data.duration);
    const duration = toDays(value, data.durationUnit);
    if (duration <= 0) return;

    onClose();
    Promise.resolve(
      onConfirm({
        startDate: data.startDate,
        platforms: data.platforms.split(',').map((p) => p.trim()).filter(Boolean),
        duration,
        durationUnit: data.durationUnit,
        durationValue: value,
        territory: data.territory.trim() || 'France',
        exclusivity: !!data.exclusivity,
      })
    ).catch((error) => {
      console.error('Error activating image rights:', error);
    });
  };

  return (
    <BaseFormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('projects.editImageRights') : t('projects.activateImageRights')}
      size="md"
      isLoading={false}
      mode="update"
      onValidate={handleSubmit(handleConfirm)}
    >
      <form onSubmit={handleSubmit(handleConfirm)} noValidate className="space-y-4">
        <p className="text-sm text-gray-600">
          {t('projects.activateImageRightsDescription')}
        </p>

        <DatePicker
          label={t('projects.diffusionStartDate')}
          value={startDate}
          onChange={(date) => setValue('startDate', date, { shouldValidate: true })}
          allowClear={false}
          error={errors.startDate?.message}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('projects.platforms')}
            {...register('platforms')}
            placeholder={t('projects.platformsPlaceholder')}
          />
          <Input
            label={t('projects.territory')}
            {...register('territory')}
            placeholder={t('projects.territoryPlaceholder')}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="grid grid-cols-2 gap-2 items-end">
            <div className="min-w-0">
              <Input
                label={t('projects.durationLabel')}
                type="number"
                {...register('duration', {
                  required: true,
                  min: { value: 1, message: t('projects.durationMin') as string },
                })}
                error={errors.duration?.message}
                placeholder={t('projects.durationPlaceholder')}
              />
            </div>
            <div className="min-w-0">
              <Select
                value={durationUnit}
                onChange={(v) => setValue('durationUnit', v as DurationUnit)}
                options={[
                  { value: 'days', label: t('projects.durationUnitDays') },
                  { value: 'weeks', label: t('projects.durationUnitWeeks') },
                  { value: 'months', label: t('projects.durationUnitMonths') },
                ]}
                className="min-w-0"
              />
            </div>
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('projects.exclusiveRights')}
            </label>
            <div className="flex items-center justify-start py-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" {...register('exclusivity')} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 peer-focus:ring-offset-0 peer-checked:bg-primary-500 transition-colors" />
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-5" />
              </label>
            </div>
          </div>
        </div>

        {endDate && (
          <p className="text-sm text-gray-500">
            {t('projects.rightsUntilPreview', {
              duration: formatRightsDurationLabel(durationDays, t),
              date: formatDateLocale(endDate),
            })}
          </p>
        )}
      </form>
    </BaseFormModal>
  );
};

export default DiffusionStartedModal;
